import { useState } from 'react';
import { Table, Card, Form, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { prsService } from '@/services/api';
import { useAsync } from '@/common/hooks/use-async';
import { AsyncBoundary } from '@/common/components/async-boundary';

const STATUS_VARIANT: Record<string, string> = {
  OPEN: 'primary', MERGED: 'success', CLOSED: 'secondary',
};

/** Human-facing Bitbucket PR URL. Prefer stored webUrl; else derive from the API prUrl. */
function prWebUrl(p: { webUrl?: string; prUrl: string }): string {
  if (p.webUrl) return p.webUrl;
  return (p.prUrl || '')
    .replace('api.bitbucket.org/2.0/repositories', 'bitbucket.org')
    .replace('/pullrequests/', '/pull-requests/');
}

export default function PrsList() {
  const [repo, setRepo] = useState('');
  const [status, setStatus] = useState('OPEN');
  // Fetch by status (global index); the repo filter is applied client-side as a
  // substring match. A DynamoDB-keyed server filter can only do exact repo, so
  // "contains" lives in the UI over the status-scoped rows.
  const { data, loading, error } = useAsync(
    () => prsService.listPrs({ status: status || undefined }),
    [status],
  );
  const needle = repo.trim().toLowerCase();
  const items = (data?.items ?? []).filter(
    (p) => !needle || (p.repo ?? '').toLowerCase().includes(needle),
  );

  return (
    <Card>
      <Card.Header>Pull requests</Card.Header>
      <Card.Body>
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Control
              placeholder="filter by repo — contains (e.g. ember)"
              value={repo}
              onChange={(e) => setRepo(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="OPEN">OPEN</option>
              <option value="MERGED">MERGED</option>
              <option value="CLOSED">CLOSED</option>
            </Form.Select>
          </Col>
        </Row>
        <AsyncBoundary loading={loading} error={error}>
          <Table hover responsive>
            <thead>
              <tr>
                <th>PR</th>
                <th>Repo</th>
                <th>Type</th>
                <th>Status</th>
                <th>Validation</th>
                <th>CVEs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.prUrl}>
                  <td>
                    <a href={prWebUrl(p)} target="_blank" rel="noopener noreferrer">
                      {p.bitbucketPrId ? `#${p.bitbucketPrId}` : p.prUrl.slice(-12)}
                    </a>
                  </td>
                  <td>{p.repo}</td>
                  <td>{p.prType ?? '-'}</td>
                  <td>
                    <Badge bg={STATUS_VARIANT[p.status ?? ''] ?? 'secondary'}>{p.status ?? '-'}</Badge>
                  </td>
                  <td>{p.validationStatus ?? '-'}</td>
                  <td>{(p.cveIds ?? []).join(', ') || '-'}</td>
                  <td>
                    <Link to={`/prs/detail?prUrl=${encodeURIComponent(p.prUrl)}`} className="small">
                      Feedback →
                    </Link>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={7} className="text-muted">No PRs.</td></tr>
              )}
            </tbody>
          </Table>
        </AsyncBoundary>
      </Card.Body>
    </Card>
  );
}
